<template>
  <section class="section news-carousel-section">
    <div class="container news-carousel-head">
      <div class="news-copy" v-reveal>
        <div class="news-en">OUR CIRCLE</div>
        <h2>{{ sectionTitle }}</h2>
        <p>{{ sectionSubtitle }}</p>
      </div>

      <div class="news-tabs" v-reveal>
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="news-tab"
          :class="{ active: activeTab === tab.key }"
          @click="setTab(tab.key)"
        >
          <span class="news-tab-line"></span>
          <span>{{ $pick(tab.label) }}</span>
        </button>
      </div>
    </div>

    <div
      ref="scroller"
      class="news-scroller"
      :class="{ dragging: isDragging }"
      @mouseenter="pauseAutoScroll"
      @mouseleave="handleMouseLeave"
      @mousedown="handleDragStart"
      @mousemove="handleDragMove"
      @mouseup="handleDragEnd"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @scroll="handleScroll"
    >
      <div class="news-track">
        <article
          v-for="(item, index) in loopedItems"
          :key="activeTab + '-' + index + '-' + $pick(item.title)"
          class="news-card"
          v-reveal
        >
          <div class="news-card-top">
            <span class="news-card-tag">{{ $pick(item.tag) }}</span>
            <span class="news-card-index">{{ cardIndex(index) }}</span>
          </div>

          <h3>{{ $pick(item.title) }}</h3>
          <p>{{ $pick(item.excerpt) }}</p>

          <div class="news-card-bottom">
            <time>{{ item.date }}</time>
            <router-link class="news-card-link" :to="item.path">
              {{ moreText }}
            </router-link>
          </div>
        </article>
      </div>
    </div>

    <div class="container news-actions">
      <router-link class="news-more" :to="activeRoute">
        <span>{{ moreButtonText }}</span>
        <span class="news-more-arrow">›</span>
      </router-link>
    </div>
  </section>
</template>

<script>
export default {
  name: "NewsTabsCarousel",
  props: {
    sourceTabs: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: "industry",
      isDragging: false,
      dragStartX: 0,
      dragStartScrollLeft: 0,
      autoTimer: null,
      boundWindowDragMove: null,
      boundWindowDragEnd: null,
      cardMeta: {
        industry: [
          {
            excerpt: {
              "zh-CN": "围绕高压及超高压输配电应用，对典型产品场景、选型原则与工程规范进行梳理。",
              "en-US": "A quick review of product applications, selection logic and engineering practices in high-voltage power transmission and distribution."
            },
            date: "2026-04-09"
          },
          {
            excerpt: {
              "zh-CN": "聚焦电压等级、典型应用范围与工程认知，帮助客户更清晰地理解产品定位。",
              "en-US": "Focused on voltage level, application scope and engineering understanding for clearer product positioning."
            },
            date: "2026-04-09"
          },
          {
            excerpt: {
              "zh-CN": "从使用环境、散热条件到安装要求，归纳干式变压器在项目中的关键要点。",
              "en-US": "Summarizing the operating environment, cooling conditions and installation requirements for dry-type transformers."
            },
            date: "2026-03-28"
          },
          {
            excerpt: {
              "zh-CN": "从器身、油箱到冷却系统，对油浸式变压器常见组成部分进行分项说明。",
              "en-US": "A structured explanation of the common components in oil-immersed transformers from tank to cooling system."
            },
            date: "2026-03-15"
          }
        ],
        company: [
          {
            excerpt: {
              "zh-CN": "记录江苏宏邦变压器有限公司参与行业活动、展示制造能力与海外市场布局的最新进展。",
              "en-US": "Following Jiangsu Hongbang Transformer Co., Ltd. at industry events, manufacturing showcases and global market expansion."
            },
            date: "2026-04-12"
          },
          {
            excerpt: {
              "zh-CN": "围绕项目交付节点与出厂检验流程，展示公司稳定的生产组织与发运能力。",
              "en-US": "Highlighting production organization, factory inspection and delivery capability around major shipping milestones."
            },
            date: "2026-04-05"
          },
          {
            excerpt: {
              "zh-CN": "通过海外项目纪实内容，展现公司在国际标准适配和工程协同上的综合能力。",
              "en-US": "A project story showing the company’s capabilities in international standards, adaptation and engineering coordination."
            },
            date: "2026-03-19"
          },
          {
            excerpt: {
              "zh-CN": "从展会现场到客户沟通场景，展示宏邦在新能源与储能应用方向的持续拓展。",
              "en-US": "From expo coverage to client-facing moments, showing Hongbang’s continued expansion into renewable and storage sectors."
            },
            date: "2026-03-03"
          }
        ]
      }
    };
  },
  computed: {
    tabs() {
      return [
        {
          key: "industry",
          label: { "zh-CN": "行业资讯", "en-US": "Industry News" }
        },
        {
          key: "company",
          label: { "zh-CN": "本站动态", "en-US": "Company News" }
        }
      ];
    },
    sectionTitle() {
      return this.locale === "zh-CN" ? "宏邦动态" : "Hongbang Updates";
    },
    sectionSubtitle() {
      return this.locale === "zh-CN"
        ? "了解最新公司动态、行业资讯和专业的售后服务团队"
        : "Discover company updates, industry insights and professional service support.";
    },
    activeRoute() {
      return this.activeTab === "industry" ? "/news/industry" : "/news/company";
    },
    moreText() {
      return this.locale === "zh-CN" ? "查看详情" : "Details";
    },
    moreButtonText() {
      return this.locale === "zh-CN" ? "查看更多" : "View More";
    },
    activeItems() {
      const titles = this.sourceTabs[this.activeTab] || [];
      const tag =
        this.activeTab === "industry"
          ? { "zh-CN": "行业资讯", "en-US": "Industry News" }
          : { "zh-CN": "本站动态", "en-US": "Company News" };

      return titles.map((title, index) => {
        const meta = (this.cardMeta[this.activeTab] || [])[index] || {};
        return {
          title: title.title || title,
          tag,
          excerpt: title.excerpt || meta.excerpt || {
            "zh-CN": "持续更新企业资讯与行业趋势内容。",
            "en-US": "Continuously updated corporate and industry content."
          },
          date: title.date || meta.date || "2026-04-20",
          path: title.path || (title.slug ? `/news/detail/${title.slug}` : this.activeTab === "industry" ? "/news/industry" : "/news/company")
        };
      });
    },
    loopedItems() {
      return [...this.activeItems, ...this.activeItems, ...this.activeItems];
    }
  },
  mounted() {
    this.boundWindowDragMove = (event) => this.handleDragMove(event);
    this.boundWindowDragEnd = () => this.handleDragEnd();
    this.$nextTick(() => {
      this.resetLoopPosition();
      this.startAutoScroll();
    });
  },
  beforeDestroy() {
    window.clearInterval(this.autoTimer);
    window.removeEventListener("mousemove", this.boundWindowDragMove);
    window.removeEventListener("mouseup", this.boundWindowDragEnd);
  },
  methods: {
    cardIndex(index) {
      return String((index % this.activeItems.length) + 1).padStart(2, "0");
    },
    setTab(tab) {
      this.activeTab = tab;
      this.pauseAutoScroll();
      this.isDragging = false;
      this.$nextTick(() => {
        this.resetLoopPosition();
        this.resumeAutoScroll();
      });
    },
    getLoopWidth() {
      const scroller = this.$refs.scroller;
      if (!scroller) {
        return 0;
      }
      return scroller.scrollWidth / 3;
    },
    resetLoopPosition() {
      const scroller = this.$refs.scroller;
      if (!scroller) {
        return;
      }

      scroller.scrollLeft = this.getLoopWidth();
    },
    normalizeScrollPosition() {
      const scroller = this.$refs.scroller;
      const loopWidth = this.getLoopWidth();

      if (!scroller || !loopWidth) {
        return;
      }

      if (scroller.scrollLeft < loopWidth * 0.5) {
        scroller.scrollLeft += loopWidth;
      } else if (scroller.scrollLeft > loopWidth * 1.5) {
        scroller.scrollLeft -= loopWidth;
      }
    },
    handleScroll() {
      this.normalizeScrollPosition();
    },
    handleDragStart(event) {
      const scroller = this.$refs.scroller;
      if (!scroller) {
        return;
      }

      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = scroller.scrollLeft;
      this.pauseAutoScroll();
      window.addEventListener("mousemove", this.boundWindowDragMove);
      window.addEventListener("mouseup", this.boundWindowDragEnd);
    },
    handleDragMove(event) {
      if (!this.isDragging) {
        return;
      }

      const scroller = this.$refs.scroller;
      if (!scroller) {
        return;
      }

      const delta = event.clientX - this.dragStartX;
      scroller.scrollLeft = this.dragStartScrollLeft - delta * 1.15;
      this.normalizeScrollPosition();
    },
    handleDragEnd() {
      if (!this.isDragging) {
        window.removeEventListener("mousemove", this.boundWindowDragMove);
        window.removeEventListener("mouseup", this.boundWindowDragEnd);
        return;
      }

      this.isDragging = false;
      window.removeEventListener("mousemove", this.boundWindowDragMove);
      window.removeEventListener("mouseup", this.boundWindowDragEnd);
      this.normalizeScrollPosition();
      this.resumeAutoScroll();
    },
    handleTouchStart(event) {
      if (!event.touches || !event.touches.length) {
        return;
      }
      this.handleDragStart({ clientX: event.touches[0].clientX });
    },
    handleTouchMove(event) {
      if (!event.touches || !event.touches.length) {
        return;
      }
      this.handleDragMove({ clientX: event.touches[0].clientX });
    },
    handleTouchEnd() {
      this.handleDragEnd();
    },
    handleMouseLeave() {
      this.handleDragEnd();
      this.resumeAutoScroll();
    },
    pauseAutoScroll() {
      window.clearInterval(this.autoTimer);
      this.autoTimer = null;
    },
    resumeAutoScroll() {
      if (this.autoTimer) {
        return;
      }
      this.startAutoScroll();
    },
    startAutoScroll() {
      window.clearInterval(this.autoTimer);
      this.autoTimer = window.setInterval(() => {
        const scroller = this.$refs.scroller;
        if (!scroller || this.isDragging) {
          return;
        }
        scroller.scrollLeft += 1.1;
        this.normalizeScrollPosition();
      }, 16);
    }
  }
};
</script>

<style scoped>
.news-carousel-section {
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(222, 233, 245, 0.55), transparent 30%),
    linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
}

.news-carousel-head {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 42px;
}

.news-copy {
  max-width: 760px;
}

.news-en {
  margin-bottom: 8px;
  font-size: clamp(48px, 6.8vw, 82px);
  font-weight: 800;
  line-height: 0.95;
  background: linear-gradient(90deg, #1252a7 0%, #1a6fcb 50%, #4f64b8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.news-copy h2 {
  margin: 0 0 14px;
  font-size: 46px;
  color: #242424;
}

.news-copy p {
  margin: 0;
  color: #a7acb5;
  font-size: 16px;
}

.news-tabs {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.news-tab {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #5e6675;
  cursor: pointer;
  transition: color 0.25s ease, transform 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease;
}

.news-tab-line {
  width: 16px;
  height: 2px;
  background: currentColor;
  opacity: 0.65;
}

.news-tab:hover,
.news-tab.active {
  color: #ffffff;
  transform: translateY(-2px);
  background: linear-gradient(135deg, #134f99, #1a6fcb 58%, #4f64b8);
  box-shadow: 0 14px 28px rgba(127, 38, 94, 0.22);
}

.news-scroller {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  cursor: grab;
  user-select: none;
  scrollbar-width: none;
}

.news-scroller::-webkit-scrollbar {
  display: none;
}

.news-scroller.dragging {
  cursor: grabbing;
}

.news-track {
  display: flex;
  gap: 26px;
  padding: 10px calc((100vw - min(var(--container-width), calc(100% - 32px))) / 2) 18px;
}

.news-card {
  position: relative;
  flex: 0 0 430px;
  min-height: 460px;
  padding: 28px 30px 26px;
  border: 1px solid rgba(227, 232, 240, 0.86);
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 254, 0.95)),
    linear-gradient(135deg, rgba(13, 76, 151, 0.04), rgba(26, 111, 203, 0.05));
  box-shadow: 0 22px 44px rgba(18, 54, 110, 0.08);
  transition: transform 0.38s ease, box-shadow 0.38s ease, border-color 0.38s ease;
}

.news-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(5, 44, 92, 0.02));
  pointer-events: none;
}

.news-card:hover {
  transform: translateY(-12px) rotate(-0.3deg);
  border-color: rgba(17, 79, 153, 0.14);
  box-shadow: 0 28px 56px rgba(18, 54, 110, 0.14);
}

.news-card-top,
.news-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.news-card-top {
  margin-bottom: 26px;
}

.news-card-tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(230, 30, 47, 0.08);
  color: #ef2b2d;
  font-size: 14px;
  font-weight: 700;
}

.news-card-index {
  color: rgba(15, 51, 96, 0.16);
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
}

.news-card h3 {
  --news-card-title-size: 34px;
  display: -webkit-box;
  overflow: hidden;
  margin: 0 0 22px;
  height: calc(var(--news-card-title-size) * 1.28 * 2);
  color: #292929;
  font-size: var(--news-card-title-size);
  line-height: 1.28;
  letter-spacing: -0.02em;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  transition: color 0.3s ease, transform 0.3s ease;
}

.news-card p {
  margin: 0;
  min-height: 120px;
  color: #7b8492;
  font-size: 17px;
  line-height: 1.9;
}

.news-card-bottom {
  margin-top: 46px;
}

.news-card time {
  color: #b0b6c1;
  font-size: 14px;
}

.news-card-link {
  color: #d9262d;
  font-weight: 600;
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.news-card:hover h3 {
  color: #0d4c97;
  transform: translateY(-2px);
}

.news-card:hover .news-card-link {
  opacity: 1;
  transform: translateX(0);
}

.news-actions {
  display: flex;
  justify-content: center;
  margin-top: 26px;
}

.news-more {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border-radius: 999px;
  color: #ffffff;
  background: linear-gradient(135deg, #164f9a, #1a6fcb 58%, #4f64b8);
  box-shadow: 0 18px 40px rgba(17, 79, 153, 0.2);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.news-more:hover {
  transform: translateY(-3px);
  box-shadow: 0 24px 46px rgba(17, 79, 153, 0.28);
}

.news-more-arrow {
  font-size: 18px;
  line-height: 1;
}

@media (max-width: 1200px) {
  .news-carousel-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .news-tabs {
    justify-content: flex-start;
  }
}

@media (max-width: 900px) {
  .news-copy h2 {
    font-size: 34px;
  }

  .news-track {
    gap: 18px;
    padding-left: 16px;
    padding-right: 16px;
  }

  .news-card {
    flex-basis: 84vw;
    min-height: 420px;
    padding: 24px 22px;
  }

  .news-card h3 {
    --news-card-title-size: 24px;
    min-height: auto;
  }

  .news-card p {
    min-height: auto;
    font-size: 15px;
    line-height: 1.8;
  }

  .news-card-bottom {
    margin-top: 28px;
  }
}

@media (max-width: 520px) {
  .news-carousel-head {
    gap: 24px;
    margin-bottom: 28px;
  }

  .news-en {
    font-size: 42px;
  }

  .news-copy h2 {
    font-size: 28px;
  }

  .news-copy p {
    font-size: 14px;
    line-height: 1.75;
  }

  .news-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    gap: 10px;
  }

  .news-tab {
    justify-content: center;
    padding: 11px 10px;
    font-size: 14px;
  }

  .news-card {
    flex-basis: 88vw;
    min-height: 360px;
    border-radius: 18px;
  }

  .news-card-top {
    margin-bottom: 18px;
  }

  .news-card h3 {
    --news-card-title-size: 21px;
  }

  .news-more {
    width: 100%;
    justify-content: center;
  }
}
</style>
